<template>
  <div class="admin-shell">
    <nav class="admin-nav">
      <div class="admin-nav-left">
        <button class="admin-nav-back" type="button" @click="router.push('/administrador/dashboard')">← Obras</button>
        <span class="admin-nav-sep">|</span>
        <span class="admin-nav-title">{{ detail?.siteName || '...' }}</span>
        <button
          v-if="detail"
          class="btn-toggle-status"
          :class="detail.disabled ? 'btn-reativar' : 'btn-suspender'"
          type="button"
          :disabled="toggling"
          @click="toggleStatus"
        >
          {{ toggling ? 'Aguarde...' : detail.disabled ? 'Reativar obra' : 'Suspender obra' }}
        </button>
        <button
          v-if="detail && detail.disabled"
          class="btn-toggle-status btn-deletar"
          type="button"
          :disabled="deleting"
          @click="softDelete"
        >
          {{ deleting ? 'Deletando...' : 'Deletar obra' }}
        </button>
      </div>
      <div class="admin-nav-right">
        <span class="admin-nav-user">{{ masterSessionStore.state.admin?.name }}</span>
        <button class="admin-nav-logout" type="button" @click="logout">Sair</button>
      </div>
    </nav>

    <div class="admin-body" v-if="detail">
      <!-- Mobile sidebar backdrop -->
      <div class="sidebar-backdrop" v-if="sidebarOpen" @click="sidebarOpen = false" />

      <!-- Sidebar -->
      <aside class="admin-sidebar" :class="{ 'admin-sidebar--open': sidebarOpen }">
        <!-- Mobile close button -->
        <div class="sidebar-mobile-header">
          <button class="sidebar-close-btn" type="button" @click="sidebarOpen = false">✕</button>
        </div>
        <!-- Navigation links -->
        <nav class="sidebar-nav">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="sidebar-link"
            active-class="sidebar-link--active"
            @click="sidebarOpen = false"
          >
            {{ item.label }}
          </router-link>
        </nav>
      </aside>

      <!-- Main content -->
      <main class="admin-main">
        <!-- Mobile hamburger bar -->
        <div class="mobile-topbar">
          <button class="hamburger-btn" type="button" @click="sidebarOpen = true">
            <span></span><span></span><span></span>
          </button>
          <span class="mobile-section-label">{{ currentNavLabel }}</span>
        </div>
        <div class="tab-content">
          <router-view />
        </div>
      </main>
    </div>

    <div v-else-if="loading" class="admin-loading">Carregando...</div>
  </div>

  <!-- Delete confirm modal -->
  <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
    <div class="modal-box">
      <h2 class="modal-title modal-title-danger">Deletar obra</h2>
      <p class="modal-body-text">
        Tem certeza que deseja deletar <strong>{{ detail?.siteName }}</strong>?<br/>
        A obra sumirá do painel principal e seu username ficará disponível.<br/>
        Esta ação pode ser revertida pela aba <em>Deletadas</em>.
      </p>
      <div class="modal-actions">
        <button class="btn-admin-ghost" type="button" @click="showDeleteConfirm = false">Cancelar</button>
        <button class="btn-admin-danger" type="button" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? 'Deletando...' : 'Confirmar deletar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, provide, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { masterSessionStore, masterFetch } from '@/store/masterSession'

interface User { username: string; name: string; role: string; roleId: string; avatar: string }
interface Project { id: string; name: string; ownerUsername: string; responsibleUsernames: string[]; createdAt: string }
interface SystemDetail {
  systemId: string
  systemUsername: string
  siteName: string
  primaryColor: string
  secondaryColor: string
  logoType: string
  customLogoText: string
  disabled: boolean
  createdAt: string
  renewalDate: string
  storageLimitMb: number
  usedMb: number
  activeUsers: number
  inactiveUsers: number
  activeProjects: number
  users: User[]
  projects: Project[]
}

export default {
  name: 'AdminSystemView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const detail = ref<SystemDetail | null>(null)
    const loading = ref(true)
    const toggling = ref(false)
    const deleting = ref(false)
    const showDeleteConfirm = ref(false)
    const sidebarOpen = ref(false)

    const systemId = computed(() => route.params.systemId as string)

    const currentNavLabel = computed(() => {
      const match = navItems.value.find(n => route.path.startsWith(n.path))
      return match?.label ?? ''
    })

    const navItems = computed(() => {
      const base = `/administrador/systems/${systemId.value}`
      return [
        { label: 'Visão Geral', path: `${base}/visao-geral` },
        { label: 'Usuários', path: `${base}/usuarios` },
        { label: 'Projetos', path: `${base}/projetos` },
        { label: 'Dados', path: `${base}/dados` },
        { label: 'Configurações', path: `${base}/configuracoes` },
        { label: 'Pagamento', path: `${base}/pagamento` },
        { label: 'Notas', path: `${base}/notas` },
        { label: 'Anúncios', path: `${base}/anuncios` },
        { label: 'Suporte', path: `${base}/suporte` },
        { label: 'Módulos', path: `${base}/modulos` },
        { label: 'Uso de IA', path: `${base}/ia` },
        { label: 'Logs', path: `${base}/logs` },
      ]
    })

    const loadDetail = async () => {
      loading.value = true
      try {
        const d = await masterFetch<SystemDetail>(`/master/systems/${systemId.value}/detail`)
        detail.value = d
      } catch { /* ignore */ } finally {
        loading.value = false
      }
    }

    onMounted(loadDetail)

    // Provide to children
    provide('adminSystemId', systemId)
    provide('adminDetail', detail)
    provide('adminDetailRefresh', loadDetail)

    const logout = () => { masterSessionStore.logout(); router.push('/administrador') }


    const toggleStatus = async () => {
      if (!detail.value) return
      toggling.value = true
      try {
        const resp = await masterFetch<{ ok: boolean; disabled: boolean }>(
          `/master/systems/${detail.value.systemId}/toggle-status`,
          { method: 'PUT' }
        )
        detail.value.disabled = resp.disabled
      } catch { /* ignore */ } finally {
        toggling.value = false
      }
    }

    const softDelete = () => {
      showDeleteConfirm.value = true
    }

    const confirmDelete = async () => {
      if (!detail.value) return
      deleting.value = true
      try {
        await masterFetch(`/master/systems/${detail.value.systemId}/soft-delete`, { method: 'PUT' })
        showDeleteConfirm.value = false
        router.push('/administrador/dashboard')
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Erro ao deletar obra.')
      } finally {
        deleting.value = false
      }
    }

    return {
      router, masterSessionStore, detail, loading,
      navItems, currentNavLabel, sidebarOpen,
      toggling, deleting, showDeleteConfirm,
      logout,
      toggleStatus, softDelete, confirmDelete
    }
  }
}
</script>

<style scoped lang="scss">
* { box-sizing: border-box; }

.admin-shell { min-height: 100vh; background: #f0f4f8; font-family: 'Inter', sans-serif; display: flex; flex-direction: column; }

.admin-nav {
  height: 56px;
  background: #0f4c81;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
}

.admin-nav-left { display: flex; align-items: center; gap: 12px; }
.admin-nav-back {
  background: rgba(255,255,255,0.12);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.22); }
}
.admin-nav-sep { color: rgba(255,255,255,0.3); }
.admin-nav-title { color: #fff; font-size: 16px; font-weight: 600; }
.admin-nav-right { display: flex; align-items: center; gap: 16px; }
.admin-nav-user { color: rgba(255,255,255,0.8); font-size: 14px; }
.admin-nav-logout {
  background: rgba(255,255,255,0.12);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.22); }
}

.admin-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.admin-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sidebar-scards {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 12px 12px;
}

.scard {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.scard-value { font-size: 16px; font-weight: 700; color: #111827; }
.scard-label { font-size: 11px; color: #6b7280; }

.sidebar-sep {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 4px 0 8px;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  padding: 0 8px;
}

.sidebar-link {
  display: block;
  padding: 9px 12px;
  border-radius: 7px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;

  &:hover { background: #f3f4f6; color: #0f4c81; }
  &.sidebar-link--active { background: #e8f0f7; color: #0f4c81; font-weight: 600; }
}

.admin-main {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.tab-content {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  overflow: hidden;
  min-height: 200px;
}

.admin-loading { text-align: center; color: #6b7280; padding: 80px 0; }

.btn-toggle-status {
  border: none;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.btn-suspender {
  background: rgba(239,68,68,0.15);
  color: #fca5a5;
  &:hover:not(:disabled) { background: rgba(239,68,68,0.25); }
}

.btn-reativar {
  background: rgba(34,197,94,0.15);
  color: #86efac;
  &:hover:not(:disabled) { background: rgba(34,197,94,0.25); }
}

.btn-deletar {
  background: rgba(239,68,68,0.25);
  color: #fca5a5;
  border: 1px solid rgba(239,68,68,0.4);
  &:hover:not(:disabled) { background: rgba(239,68,68,0.4); }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 14px;
  padding: 32px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 14px;
}

.modal-title-danger { color: #dc2626; }

.modal-body-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 24px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-admin-ghost {
  background: transparent;
  color: #374151;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
}

.btn-admin-danger {
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #b91c1c; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

/* ── Mobile hamburger topbar ── */
.mobile-topbar { display: none; }

.hamburger-btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  flex-shrink: 0;

  span {
    display: block;
    height: 2px;
    background: #374151;
    border-radius: 2px;
    width: 100%;
  }

  &:hover { background: #f3f4f6; }
}

.mobile-section-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.sidebar-mobile-header { display: none; }

.sidebar-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 199;
}

@media (max-width: 768px) {
  .admin-nav-sep,
  .admin-nav-title { display: none; }

  .admin-nav { padding: 0 16px; }

  .mobile-topbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px 0;
  }

  .admin-main { padding: 0 0 24px; }

  .tab-content { border-radius: 0; box-shadow: none; margin: 12px 16px 0; }

  .admin-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 260px;
    z-index: 200;
    transform: translateX(-100%);
    transition: transform 0.22s ease;
    box-shadow: 4px 0 24px rgba(0,0,0,0.15);
    padding-top: 0;

    &.admin-sidebar--open { transform: translateX(0); }
  }

  .sidebar-backdrop { display: block; }

  .sidebar-mobile-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 12px 4px;
  }

  .sidebar-close-btn {
    background: transparent;
    border: none;
    font-size: 18px;
    color: #6b7280;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    &:hover { background: #f3f4f6; color: #374151; }
  }
}
</style>
